/**
 * CCGiftSent — gift-mode terminal screen (replaces CC6Computing/CC7Result).
 * data-screen-label="cc-gift Sent"
 *
 * The gift's reveal is computed for and delivered to the RECIPIENT (in the bot,
 * after they open the share link and consent), so the buyer has nothing to poll
 * for here. This screen hands the buyer the share link to forward; the worker
 * separately DMs them an anonymous "secret-admirer" copy once the recipient opens.
 *
 * Mirrors CC3Invite's orb + link-row + send-CTA layout (same Compat.module.css).
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { shareLink } from '../../../lib/share';
import { CCBackdrop } from './shared';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import type { UseCompatFlowReturn } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

interface CCGiftSentProps {
  flow: UseCompatFlowReturn;
}

export function CCGiftSent({ flow }: CCGiftSentProps): JSX.Element {
  const { t } = useTranslation(['flows', 'common']);
  const navigate = useNavigate();
  const { inviteLink, partner, searchUsername } = flow;

  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolved partner (on-platform) → display name; off-platform gift → @handle.
  const recipientHandle = partner?.username ?? searchUsername;
  const recipientName = partner?.displayName ?? (recipientHandle ? `@${recipientHandle}` : '');
  const link = inviteLink ?? '';

  function handleCopy() {
    if (!link) return;
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleSendTelegram() {
    if (!link) return;
    // Forward the gift link to the recipient via Telegram's share sheet — NOT
    // openTelegramLink(link), which would open the bot chat for the sender.
    shareLink(link, t('compat.gift.sentBody', { name: recipientName }));
  }

  function handleDone() {
    void navigate('/');
  }

  return (
    <div className={styles.root} data-screen-label="cc-gift Sent">
      <CCBackdrop tone="pair" />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('flows:compat.gift.navBack')} onBack={handleDone} headingId="compat-heading" />

        <div className={styles.inviteCenter}>
          {/* Icon */}
          <div className={styles.inviteOrb}>🎁</div>

          {/* Copy text */}
          <div style={{ textAlign: 'center' }}>
            <div className={styles.eyebrowSuccess}>✓ {t('compat.gift.sentEyebrow')}</div>
            <h1 className={styles.displayTitleSm}>{t('compat.gift.sentTitle')}</h1>
            <p className={styles.bodyText} style={{ textAlign: 'center', padding: '0 8px', marginTop: 10 }}>
              {t('compat.gift.sentBody', { name: recipientName })}
            </p>
          </div>

          {/* Gift link + copy */}
          <div className={styles.inviteLinkWrap}>
            <div className={styles.inviteLinkLabel}>{t('compat.gift.linkLabel')}</div>
            <div className={styles.inviteLinkRow}>
              <span className={styles.inviteLinkText}>{link}</span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopy}
                data-testid="ccgift-copy-btn"
              >
                {copied ? `✓ ${t('compat.gift.copied')}` : t('compat.gift.copy')}
              </button>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            onClick={handleSendTelegram}
            data-testid="ccgift-send-btn"
          >
            ↗ {t('compat.gift.sendCta')}
          </PrimaryButton>
          <button
            type="button"
            className={styles.ghostLinkBtn}
            style={{ marginTop: 12, alignSelf: 'center' }}
            onClick={handleDone}
            data-testid="ccgift-done-btn"
          >
            {t('compat.gift.doneCta')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CCGiftSent;
